import { createStore } from '../store/store.js';

/**
 * Type defining the modality options for input detection.
 * @internal
 */
export type Modality = 'keyboard' | 'pointer' | 'touch' | 'unknown';

/**
 * Module-level store for the current input modality.
 * Tracks whether the user is interacting via keyboard, pointer (mouse), or touch.
 * @internal
 */
export const modalityStore = createStore<Modality>('unknown');

// list of modifier keys commonly used in a mouse or keyboard combination,
// see https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values#modifier_keys.
const ignoreMap = [
  'Alt',
  'AltGraph',
  'CapsLock',
  'Control',
  'FnLock',
  'Fn',
  'Hyper',
  'Meta', // Command (Mac), in some browsers also Windows-key
  'NumLock',
  'OS', // Windows-key
  'ScrollLock',
  'Super', // Virtual keyboard key
  'Symbol',
  'SymbolLock',
];

/** Look-up map of all event types and the corresponding modalities. */
const eventTypeMap: Record<string, string> = {
  keydown: 'keyboard',
  keyup: 'keyboard',
  mousedown: 'mouse',
  mousemove: 'mouse',
  MSPointerDown: 'pointer',
  MSPointerMove: 'pointer',
  pointerdown: 'pointer',
  pointermove: 'pointer',
  touchstart: 'touch',
  touchend: 'touch',
  wheel: 'wheel',
};

/**
 * All navigational keys that should be considered keyboard users,
 * see https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values#navigation_keys
 */
const navigationalKeys = [
  'Tab',
  'Esc',
  'Escape',
  ' ',
  'ArrowDown',
  'Down',
  'ArrowUp',
  'Up',
  'ArrowLeft',
  'Left',
  'ArrowRight',
  'Right',
  'End',
  'Home',
  'PageDown',
  'PageUp',
];

/**
 * Handles keyboard events and updates the modality store accordingly.
 * Modifier keys are ignored, and only navigational keys or events without a target
 * trigger a keyboard modality.
 */
function handleKeyboardInput(event: KeyboardEvent): void {
  if (ignoreMap.includes(event.key)) {
    modalityStore.setState('pointer');
    return;
  }

  if (!event.target || navigationalKeys.includes(event.key)) {
    modalityStore.setState('keyboard');
  }
}

/**
 * Handles pointer/mouse/touch events and updates the modality store accordingly.
 */
function handlePointerInput(event: MouseEvent | PointerEvent): void {
  const modality = eventTypeMap[event.type];

  switch (modality) {
    case 'touch':
    case 'pen':
      modalityStore.setState('touch');
      break;
    case 'mouse':
    case 'pointer':
    case 'wheel':
      modalityStore.setState('pointer');
      break;
  }
}

let isInitialized = false;

/**
 * Initializes input modality detection by attaching event listeners to the window.
 * This function is idempotent - calling it multiple times will only initialize once.
 *
 * The modality detection tracks whether the user is currently using keyboard, pointer (mouse),
 * or touch input, updating the global `modalityStore` accordingly.
 *
 * @internal
 */
export function initModalityDetection() {
  if (isInitialized || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('pointerdown', handlePointerInput);
  // we need to rely on the keydown event (already in the capture phase) here so we don't break usages where the event default behavior is prevented or propagation is stopped,
  // which would otherwise get the wrong, initial, modality (unknown); by using the capture phase, we can ensure we set the modality correctly before the other events occur
  window.addEventListener('keydown', handleKeyboardInput, { capture: true });
  window.addEventListener('wheel', handlePointerInput);

  isInitialized = true;
}
